/**
 * useKleedFotos — wasserijfoto's van de kleden op een stop.
 *
 * Voor de afleverstop: de chauffeur ziet vóór hij aanbelt wat voor kleed hij
 * uit de bus moet halen (minder kans op het verkeerde kleed). Per kleed één
 * thumbnail (nieuwste foto uit de klant-fasen), plus alle foto's voor de
 * lightbox. Leest de media-laag via `GET /api/media?wiSfId=`, zelfde pad en
 * headers als MediaStrip. Stil falen: de foto is hulp, geen voorwaarde.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores';
import type { LightboxFoto } from '../MediaLightbox';

interface MediaRij {
  id: string;
  url: string;
  type: 'foto' | 'video';
  fase: string;
  taken_at: string;
}

/** Zelfde set als MediaStrip.KLANT_FASEN: het kleed zelf, in de wasserij. */
const KLEED_FASEN = ['voorinspectie', 'reinigen', 'na-inspectie'];

/**
 * De uit CompanyCam geimporteerde historie. Die telt NIET mee in de
 * klant-fotonummering ("zie foto 3" moet op elk scherm hetzelfde kleed-nummer
 * zijn, zie MediaStrip.KLANT_FASEN) maar helpt wel bij herkennen: voor veel
 * kleden is dit het enige beeld dat er is. Daarom apart gelabeld, achteraan.
 */
const ARCHIEF_FASE = 'import';

export interface KleedFoto {
  wiSfId: string;
  /** Nieuwste foto van dit kleed, of null als er geen is. */
  thumb: string | null;
  /** Media-id van `thumb`, zodat de lightbox op de juiste foto opent. */
  thumbId: string | null;
  /** Klant-fasen (genummerd) gevolgd door archief, elk oud → nieuw. */
  fotos: LightboxFoto[];
}

export function useKleedFotos(wiSfIds: string[]): Record<string, KleedFoto> {
  const [state, setState] = useState<Record<string, KleedFoto>>({});
  const sleutel = wiSfIds.join(',');

  useEffect(() => {
    if (!sleutel) return;
    let cancelled = false;
    const { staff, pin } = useAuthStore.getState();
    if (!staff?.id || !pin) return;
    const headers = { 'X-Staff-Id': staff.id, 'X-Staff-Pin': pin };

    async function laad(id: string): Promise<KleedFoto> {
      try {
        const res = await fetch(`/api/media?wiSfId=${encodeURIComponent(id)}`, { headers });
        if (!res.ok) return { wiSfId: id, thumb: null, thumbId: null, fotos: [] };
        const data = (await res.json()) as { success: boolean; media: MediaRij[] };
        if (!data.success) return { wiSfId: id, thumb: null, thumbId: null, fotos: [] };
        const opTijd = (a: MediaRij, b: MediaRij) => a.taken_at.localeCompare(b.taken_at);
        const fotos = data.media.filter((m) => m.type === 'foto');
        const rijen = fotos.filter((m) => KLEED_FASEN.includes(m.fase)).sort(opTijd);
        const archief = fotos.filter((m) => m.fase === ARCHIEF_FASE).sort(opTijd);
        // Nieuwste klant-foto wint; is die er niet, dan de nieuwste archieffoto.
        const thumbRij = rijen[rijen.length - 1] ?? archief[archief.length - 1] ?? null;
        return {
          wiSfId: id,
          thumb: thumbRij?.url ?? null,
          thumbId: thumbRij?.id ?? null,
          fotos: [
            ...rijen.map((m, i) => ({ id: m.id, url: m.url, label: `Foto ${i + 1}` })),
            ...archief.map((m, i) => ({ id: m.id, url: m.url, label: `Archief ${i + 1}` })),
          ],
        };
      } catch {
        return { wiSfId: id, thumb: null, thumbId: null, fotos: [] };
      }
    }

    void Promise.all(sleutel.split(',').map(laad)).then((lijst) => {
      if (cancelled) return;
      setState(Object.fromEntries(lijst.map((k) => [k.wiSfId, k])));
    });
    return () => {
      cancelled = true;
    };
  }, [sleutel]);

  return state;
}
